import { useLazyGetResourceQuery } from '@/store/api/diagnosticApi';
import { useCallback, useEffect, useState } from 'react';

export const useFetchResource = (resourceId: string | null) => {
  const [fetchResource, { data, isLoading, isFetching, isError, error }] = useLazyGetResourceQuery();
  const [resource, setResource] = useState<string>('');
  const [resourceType, setResourceType] = useState<string>('');

  useEffect(() => {
    if (resourceId) {
      fetchResource(
        { resource_id: resourceId, signed_url: true },
        true // preferCacheValue: use cached data if available (1h TTL)
      );
    } else {
      setResource('');
      setResourceType('');
    }
  }, [resourceId, fetchResource]);

  useEffect(() => {
    if (data && resourceId) {
      setResourceType(data.type);

      // Signed URL path: use the URL directly (no base64 conversion needed)
      if (data.signed_url) {
        setResource(data.signed_url);
        return;
      }

      // Legacy fallback: base64 content
      if (data.content) {
        if (data.type.startsWith('video/')) {
          try {
            // Convert base64 to blob using chunked processing to reduce peak memory
            const byteString = atob(data.content);
            const CHUNK_SIZE = 512 * 1024; // 512KB chunks
            const chunks: Uint8Array[] = [];

            for (let offset = 0; offset < byteString.length; offset += CHUNK_SIZE) {
              const end = Math.min(offset + CHUNK_SIZE, byteString.length);
              const chunk = new Uint8Array(end - offset);
              for (let i = 0; i < chunk.length; i++) {
                chunk[i] = byteString.charCodeAt(offset + i);
              }
              chunks.push(chunk);
            }

            const blob = new Blob(chunks, { type: data.type });
            const blobUrl = URL.createObjectURL(blob);
            setResource(blobUrl);
          } catch (err) {
            console.error('Failed to create blob URL:', err);
            setResource(`data:${data.type};base64, ${data.content}`);
          }
        } else {
          setResource(`data:${data.type};base64, ${data.content}`);
        }
      }
    }
  }, [data, resourceId]);

  // Cleanup blob URLs when component unmounts or resource changes
  useEffect(() => {
    return () => {
      if (resource.startsWith('blob:')) {
        URL.revokeObjectURL(resource);
      }
    };
  }, [resource]);

  // Manual retry: force a fresh network request (bypass cache)
  const retry = useCallback(() => {
    if (resourceId) {
      fetchResource({ resource_id: resourceId, signed_url: true }, false);
    }
  }, [resourceId, fetchResource]);

  return { resource, resourceType, isLoadingResource: isLoading || isFetching, isError, error, retry };
};
